import { useState } from "react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import { Loader2 } from "lucide-react";

/**
 * Controlled destructive-confirmation dialog (replaces window.confirm).
 * Pass open/onOpenChange + onConfirm (may be async). Shows a spinner while confirming.
 */
export default function ConfirmDialog({
  open, onOpenChange, onConfirm, title = "Are you sure?", description,
  confirmLabel = "Delete", cancelLabel = "Cancel", destructive = true, testid = "confirm-dialog",
}) {
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm?.();
      onOpenChange?.(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(v) => !busy && onOpenChange?.(v)}>
      <AlertDialogContent className="border-sky bg-white" data-testid={testid}>
        <AlertDialogHeader>
          <AlertDialogTitle className="font-sora text-navy">{title}</AlertDialogTitle>
          {description && <AlertDialogDescription className="text-ink/60">{description}</AlertDialogDescription>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <button
            data-testid={`${testid}-cancel`}
            onClick={() => onOpenChange?.(false)}
            disabled={busy}
            className="rounded-xl border border-sky px-4 py-2 text-sm font-medium text-ink/70 hover:bg-mist disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            data-testid={`${testid}-confirm`}
            onClick={handleConfirm}
            disabled={busy}
            className={`flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 ${destructive ? "bg-red-500 hover:bg-red-600" : "bg-loop hover:bg-loophover"}`}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : confirmLabel}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
